import { authHeader, BASE_URL, handleResponse, handleResponseBlob } from '@helpers/api';
import { visibilityMockService } from './visibilityMock';
import { ProductEvent } from '@constants/product-events';
import { captureOnSuccess } from '@helpers/product-events';

const useVisibilityMock =
    import.meta.env.MODE === 'development' && import.meta.env.REACT_APP_VISIBILITY_MOCK === 'true';

export const analyticsService = {
    getAnalyticsConversion,
    exportAnalyticsCSV,
    getAnalyticsReviews,
    getAnalyticsFeedback,
    getAnalyticsConversations,
    getAnalyticsSocialPosts,
    getAnalyticsPresence,
    getOperationalInsights,
    getVisibilityStatus,
    getVisibilitySummary,
    getVisibilityLocations,
    exportVisibilityLocations,
    getVisibilityLocation,
    getVisibilityCompetitor,
    getVisibilitySettings,
    updateVisibilitySettings,
    setVisibilityAutoScan,
    rescanVisibilityLocation,
};

function getAnalyticsConversion(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/conversion`, requestOptions).then(handleResponse);
}

function exportAnalyticsCSV(query, type, params, format = 'csv') {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(query),
    };
    const queryParams = new URLSearchParams();
    if (params) queryParams.set('graph', params);
    queryParams.set('format', format);
    return fetch(`${BASE_URL}api/analytics/${type}/export?${queryParams}`, requestOptions)
        .then(handleResponseBlob)
        .then(captureOnSuccess(ProductEvent.DataExported, { resource: 'analytics', report: type }));
}

function getAnalyticsReviews(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/reviews`, requestOptions).then(handleResponse);
}

function getAnalyticsFeedback(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/feedbacks`, requestOptions).then(handleResponse);
}

function getAnalyticsConversations(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/conversations`, requestOptions).then(handleResponse);
}

function getAnalyticsSocialPosts(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/social-posts`, requestOptions).then(handleResponse);
}

function getAnalyticsPresence(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/presence`, requestOptions).then(handleResponse);
}

function getOperationalInsights(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/analytics/operational-insights`, requestOptions).then(handleResponse);
}

function getVisibilityStatus() {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilityStatus();
    }

    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/visibility/status`, requestOptions).then(handleResponse);
}

function getVisibilitySummary(data) {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilitySummary(data);
    }

    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/visibility/summary`, requestOptions).then(handleResponse);
}

function getVisibilityLocations(data, { sort, dir, page, size }) {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilityLocations(data, { sort, dir, page, size });
    }

    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };
    const queryParams = new URLSearchParams({ sort, dir, page, size });

    return fetch(`${BASE_URL}api/visibility/locations?${queryParams}`, requestOptions).then(handleResponse);
}

function exportVisibilityLocations(data, { sort, dir }, format = 'csv') {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };
    const queryParams = new URLSearchParams({ sort, dir, format });

    return fetch(`${BASE_URL}api/visibility/locations/export?${queryParams}`, requestOptions)
        .then(handleResponseBlob)
        .then(captureOnSuccess(ProductEvent.DataExported, { resource: 'visibility' }));
}

function getVisibilityLocation(id, scanId) {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilityLocation(id, scanId);
    }

    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };
    const query = scanId ? `?${new URLSearchParams({ scanId })}` : '';

    return fetch(`${BASE_URL}api/visibility/locations/${id}${query}`, requestOptions).then(handleResponse);
}

function getVisibilityCompetitor(locationId, placeId, scanId) {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilityCompetitor(locationId, placeId, scanId);
    }

    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };
    const query = scanId ? `?${new URLSearchParams({ scanId })}` : '';

    return fetch(
        `${BASE_URL}api/visibility/locations/${locationId}/competitors/${encodeURIComponent(placeId)}${query}`,
        requestOptions,
    ).then(handleResponse);
}

function getVisibilitySettings(id) {
    if (useVisibilityMock) {
        return visibilityMockService.getVisibilitySettings(id);
    }

    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/visibility/locations/${id}/settings`, requestOptions).then(handleResponse);
}

function updateVisibilitySettings(id, data) {
    if (useVisibilityMock) {
        return visibilityMockService.updateVisibilitySettings(id, data);
    }

    const requestOptions = {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/visibility/locations/${id}/settings`, requestOptions).then(handleResponse);
}

function setVisibilityAutoScan(id, enabled) {
    if (useVisibilityMock) {
        return visibilityMockService.setVisibilityAutoScan(id, enabled);
    }

    const requestOptions = {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({ enabled }),
    };

    return fetch(`${BASE_URL}api/visibility/locations/${id}/auto-scan`, requestOptions).then(response =>
        response.ok ? response : handleResponse(response),
    );
}

function rescanVisibilityLocation(id) {
    if (useVisibilityMock) {
        return visibilityMockService.rescanVisibilityLocation(id);
    }

    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/visibility/locations/${id}/rescan`, requestOptions).then(response => {
        if (response.ok) {
            return response;
        }
        if (response.status === 409) {
            return Promise.reject({ status: response.status });
        }
        return handleResponse(response);
    });
}
